import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Unlock, X } from "lucide-react";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { API } from "@/config";

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (k) => new Date(k + "T12:00:00");
const fmt = (k) => fromKey(k).toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
const fmtLong = (k) => fromKey(k).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

export default function ClosuresPanel({ headers }) {
  const [closures, setClosures] = useState(null);
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/closures`, { headers })
      .then((res) => setClosures(res.data))
      .catch(() => toast.error("No se pudieron cargar los cierres"));
  }, []);

  const loadAvail = (k) =>
    axios
      .get(`${API}/appointments/availability?date=${k}`)
      .then((r) => setAvail(r.data))
      .catch(() => toast.error("No se pudo cargar el día"));

  useEffect(() => {
    if (date) {
      setAvail(null);
      loadAvail(toKey(date));
    }
  }, [date]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/closures`, next, { headers });
      setClosures(res.data);
      if (date) await loadAvail(toKey(date));
      toast.success("Cambios guardados en tu web");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!closures) return <div className="mt-8 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const key = date ? toKey(date) : null;
  const dayClosed = key && closures.dates.includes(key);
  const daySlots = key ? closures.slots.filter((s) => s.date === key).map((s) => s.time) : [];

  const toggleDay = () =>
    persist({
      dates: dayClosed ? closures.dates.filter((d) => d !== key) : [...closures.dates, key],
      slots: closures.slots,
    });

  const toggleSlot = (t) =>
    persist({
      dates: closures.dates,
      slots: daySlots.includes(t)
        ? closures.slots.filter((s) => !(s.date === key && s.time === t))
        : [...closures.slots, { date: key, time: t }],
    });

  const removeSlot = (s) =>
    persist({ dates: closures.dates, slots: closures.slots.filter((x) => !(x.date === s.date && x.time === s.time)) });

  const upcomingDates = closures.dates.filter((k) => fromKey(k) >= today);
  const upcomingSlots = closures.slots.filter((s) => fromKey(s.date) >= today);
  const weekdaySlots = avail?.slots || [];

  return (
    <div data-testid="closures-panel" className="mt-8 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-6">
      <div>
        <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Cerrar días u horas concretas</p>
        <p className="text-sm text-taupe mt-1">
          Elige un día en el calendario: podrás cerrarlo completo o cerrar solo algunas horas. Vuelve a tocar para abrir.
        </p>
      </div>

      <div className="grid lg:grid-cols-[auto,1fr] gap-6">
        <div className="rounded-xl border border-sand bg-cream w-fit" data-testid="closures-calendar">
          <Calendar
            mode="single"
            locale={es}
            selected={date}
            onSelect={setDate}
            disabled={{ before: today }}
            modifiers={{ closed: closures.dates.map(fromKey), partial: [...new Set(closures.slots.map((s) => s.date))].map(fromKey) }}
            modifiersClassNames={{ closed: "line-through text-red-600", partial: "underline decoration-gold decoration-2" }}
          />
        </div>

        <div className="min-w-0">
          {!date ? (
            <p className="text-sm text-taupe">Selecciona un día para gestionarlo.</p>
          ) : (
            <div className="grid gap-4" data-testid="closures-day-editor">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-display text-xl text-obsidian capitalize">{fmtLong(key)}</p>
                <button
                  onClick={toggleDay}
                  disabled={saving}
                  data-testid="closures-toggle-day-btn"
                  className={`inline-flex items-center gap-2 rounded-xl text-sm font-medium px-5 py-2.5 transition-colors disabled:opacity-50 ${
                    dayClosed ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-obsidian hover:bg-cocoa text-white"
                  }`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : dayClosed ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {dayClosed ? "Abrir el día completo" : "Cerrar el día completo"}
                </button>
              </div>

              {dayClosed ? (
                <p data-testid="closures-day-closed-msg" className="text-sm text-red-700">Este día está cerrado: nadie puede reservar.</p>
              ) : !avail ? (
                <Loader2 className="w-5 h-5 text-gold animate-spin" />
              ) : weekdaySlots.length === 0 ? (
                <p className="text-sm text-taupe">Este día de la semana no trabajas según tu horario semanal.</p>
              ) : (
                <div>
                  <p className="text-xs text-taupe mb-2">Toca una hora para cerrarla o abrirla. Las horas con cita aparecen marcadas.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {weekdaySlots.map((t) => {
                      const booked = avail.taken.includes(t);
                      const closed = daySlots.includes(t);
                      return (
                        <button
                          key={t}
                          onClick={() => toggleSlot(t)}
                          disabled={saving}
                          data-testid={`closures-slot-${t.replace(":", "")}`}
                          className={`relative rounded-lg border px-2 py-2 text-sm transition-colors duration-200 disabled:opacity-50 ${
                            closed
                              ? "border-red-300 bg-red-50 text-red-700 line-through"
                              : "border-sand bg-cream text-cocoa hover:border-gold/60"
                          }`}
                        >
                          {t}
                          {booked && (
                            <span className="absolute -top-1.5 -right-1.5 rounded-full bg-gold text-white text-[10px] px-1.5 py-0.5">cita</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {(upcomingDates.length > 0 || upcomingSlots.length > 0) && (
        <div className="grid gap-3 border-t border-sand pt-5">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Próximos cierres</p>
          <div className="flex flex-wrap gap-2">
            {upcomingDates.map((k) => (
              <button
                key={k}
                onClick={() => persist({ dates: closures.dates.filter((d) => d !== k), slots: closures.slots })}
                data-testid={`closure-day-chip-${k}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs px-3 py-1.5 capitalize hover:bg-red-100 transition-colors"
              >
                {fmt(k)} · día completo <X className="w-3 h-3" />
              </button>
            ))}
            {upcomingSlots.map((s) => (
              <button
                key={`${s.date}-${s.time}`}
                onClick={() => removeSlot(s)}
                data-testid={`closure-slot-chip-${s.date}-${s.time.replace(":", "")}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-rosesoft text-cocoa text-xs px-3 py-1.5 capitalize hover:bg-sand transition-colors"
              >
                {fmt(s.date)} · {s.time} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
